import { BffResponseObject } from 'sources/model/response';
import { PzOverallPerformance, PzPerformanceProgramBreakdown } from './../models/pz';
import { MetricBrandDetails, MetricsDetails } from 'src/sources/model/pz-response';
import { Store } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import { Logger } from 'bernie-logger';
import { PartnerPerfSource } from 'sources/partner-perf-source';
import { makeObservable, observable, flow, computed, action } from 'mobx';
import { ClientPzMetricAnalyticsRequest } from 'models';
import { PzCurrenciesDetails } from 'src/models/pz';

import * as _groupBy from 'lodash.groupby';
import { Progress } from 'src/models/common';
import moment from 'moment';

export class PartnerPerfStore extends Store {
  public analytics: MetricsDetails[];
  public analyticsState: Progress = 'pending';
  public customDateRange: boolean;
  public startDate: string;
  public endDate: string;
  public currency: string;
  public currencies: PzCurrenciesDetails[];
  public selectedDayRange: string;

  private readonly partnerPerfSource: PartnerPerfSource;

  public constructor(partnerPerfSource: PartnerPerfSource, state: SerializedData = {}, logger: Logger) {
    super(state, logger);
    this.partnerPerfSource = partnerPerfSource;

    makeObservable(this, {
      analytics: observable,
      analyticsState: observable,
      customDateRange: observable,
      setCustomDateRange: action,
      startDate: observable,
      endDate: observable,
      selectedDayRange: observable,
      currency: observable,
      activeCurrency: computed,
      fetchPzAnalytics: flow,
      overallPerformance: computed,
      setSelectedTimePeriod: action,
      setSelectedCurrency: action,
      timePeriod: computed,
      getFetchingState: computed,
      getProgramMetricsBreakdown: computed,
    });

    this.analytics = [];
    // initial date time period
    const currentDateEndOfTime = moment().endOf('day').utc();
    this.customDateRange = false;
    this.endDate = currentDateEndOfTime.format();
    this.startDate = currentDateEndOfTime.subtract(7, 'days').startOf('day').format();
    this.currencies = this.partnerPerfSource.getAvaliablePzCurrencies();
    this.currency = 'USD';
  }

  public hydrate(data: SerializedData): void {
    if (data.partnerPerfSource) {
      // hydrating the client partnerPerfSource with server side data
      Object.assign(this.partnerPerfSource, {
        availablePzProgramsNames: data.partnerPerfSource.availablePzProgramsNames || [],
        availablePzPrograms: data.partnerPerfSource.availablePzPrograms || [],
        availableCurrencies: data.partnerPerfSource.availableCurrencies || [],
      });
    }

    /*
     delete the data.partnerPerfSouce since this.partnerPerfSource has been updated in the code above.
     Deletion prevents overwrites of client partnerPerfSource.
    */
    delete data.partnerPerfSource;
    Object.assign(this, data);
  }

  public *fetchPzAnalytics(metricRequest: ClientPzMetricAnalyticsRequest) {
    this.analyticsState = 'pending';
    try {
      const newPzAnalytics: BffResponseObject<MetricsDetails[]> = yield this.partnerPerfSource.getPzAnalytics(
        metricRequest,
      );
      this.analytics = newPzAnalytics.resBody;
      this.analyticsState = 'done';
    } catch (error) {
      this.analyticsState = 'error';
    }
  }

  /**
   * get the overall performance for all programs that user has joined
   */
  get overallPerformance(): PzOverallPerformance {
    const performance: PzOverallPerformance = {
      totalClicks: 0,
      totalConversions: 0,
      totalOrderValue: 0,
      totalPartnerCommission: 0,
    };
    this.analytics.forEach((metric) => {
      performance.totalClicks += metric.total_clicks ?? 0;
      performance.totalConversions += metric.total_conversions ?? 0;
      performance.totalOrderValue += metric.total_order_value ?? 0;
      performance.totalPartnerCommission += metric.total_partner_commission ?? 0;
    });

    return performance;
  }

  /**
   * get the program metrics breakdown that is grouped by brand
   * @param programs programs from program store
   * @returns program metrics breakdown used for table component
   */
  get getProgramMetricsBreakdown(): PzPerformanceProgramBreakdown[] {
    const pzPrograms = this.partnerPerfSource.getAvailablePzPrograms();

    // adding title and brand info to each metric detail from programs
    const programMetrics: MetricBrandDetails[] = this.analytics.map((datum) => {
      const pwaProgram = pzPrograms.find((program) => program.name === datum.campaign_name);

      return {
        ...datum,
        title: pwaProgram.title,
        brand: pwaProgram.brand,
        brandLogo: pwaProgram.img,
      };
    });

    //group metrics by brand
    const metricsGroupedByBrand: { [key: string]: MetricBrandDetails[] } = _groupBy(programMetrics, 'brand');

    //modify data to suit program breakdown data structure
    const allBrands = Object.keys(metricsGroupedByBrand);
    const programMetricsBreakDown: PzPerformanceProgramBreakdown[] = allBrands.map((brand) => {
      const performance: PzOverallPerformance = {
        totalClicks: 0,
        totalConversions: 0,
        totalOrderValue: 0,
        totalPartnerCommission: 0,
      };
      const programsBrand = metricsGroupedByBrand[brand];
      programsBrand.forEach((datum) => {
        performance.totalClicks += datum.total_clicks ?? 0;
        performance.totalConversions += datum.total_conversions ?? 0;
        performance.totalOrderValue += datum.total_order_value ?? 0;
        performance.totalPartnerCommission += datum.total_partner_commission ?? 0;
      });

      return {
        brandTitle: brand,
        programs: programsBrand,
        totalClicks: performance.totalClicks,
        totalConversions: performance.totalConversions,
        totalOrderValue: performance.totalOrderValue,
        totalPartnerCommission: performance.totalPartnerCommission,
      };
    });

    return programMetricsBreakDown;
  }

  /**
   * set/update time period selected
   * @param startDate dateTime UTC in string
   * @param endDate dateTime UTC in string
   */
  public setSelectedTimePeriod(startDate: string, endDate: string) {
    this.startDate = startDate;
    this.endDate = endDate;
  }

  /**
   * Get timePeriod
   */
  get timePeriod() {
    return {
      startDate: moment(this.startDate).toDate(),
      endDate: moment(this.endDate).toDate(),
    };
  }

  public setSelectedCurrency(currency) {
    this.currency = currency;
  }

  get activeCurrency() {
    return this.currencies.find((p) => p.iso === this.currency);
  }

  /**
   * Get fetching progress
   */
  get getFetchingState() {
    return this.analyticsState;
  }

  public setCustomDateRange(customDateRange: boolean) {
    this.customDateRange = customDateRange;
  }
}
